import React, { useState } from "react";
import PropTypes from "prop-types";

export const SaveFiltersModal = ({
  isOpen,
  onClose,
  onSave,
  presetName,
  onPresetNameChange,
  currentFilterQuery = null,
  currentCars = [],
  currentEvents = [],
  currentSubsystems = [],
  currentConsist = null,
  isSaving = false
}) => {
  const [validationError, setValidationError] = useState('');

  const hasFilterQuery = () => {
    if (!currentFilterQuery) {
      return false;
    }

    if (typeof currentFilterQuery === 'string') {
      return currentFilterQuery.trim().length > 0;
    }

    if (typeof currentFilterQuery === 'object') {
      if (Array.isArray(currentFilterQuery.rules)) {
        return currentFilterQuery.rules.length > 0;
      }
      return Object.keys(currentFilterQuery).length > 0;
    }

    return false;
  };

  const getFilterQueryPreview = () => {
    if (!hasFilterQuery()) {
      return 'No environment variables query configured';
    }

    if (typeof currentFilterQuery === 'string') {
      return currentFilterQuery;
    }

    try {
      return JSON.stringify(currentFilterQuery, null, 2);
    } catch {
      return 'Invalid filter query format';
    }
  };

  if (!isOpen) {
    return null;
  }

  const validatePresetName = (name) => {
    if (!name?.trim()) {
      return 'Please enter a preset name';
    }
    if (name.trim().length > 100) {
      return 'Preset name must not exceed 100 characters';
    }
    // Check for invalid characters (allow letters, numbers, spaces, hyphens, underscores)
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
      return 'Preset name can only contain letters, numbers, spaces, hyphens, and underscores';
    }
    return '';
  };

  const hasSelectedFilters = () => Boolean(
    hasFilterQuery() ||
    currentConsist ||
    (currentEvents && currentEvents.length > 0) ||
    (currentCars && currentCars.length > 0) ||
    (currentSubsystems && currentSubsystems.length > 0)
  );

  const handleNameChange = (value) => {
    onPresetNameChange(value);
    const error = validatePresetName(value);
    setValidationError(error);
  };

  const handleSave = () => {
    const error = validatePresetName(presetName);
    if (error) {
      setValidationError(error);
      return;
    }

    if (!hasSelectedFilters()) {
      alert('No filters to save. Please select at least one filter first.');
      return;
    }

    onSave();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && presetName && !isSaving) {
      handleSave();
    }
  };

  return (
    <div className="save-filters-modal" data-testid="save-filters-modal">
      <div className="modal-backdrop fade in"></div>
      <div className="modal fade in" style={{ display: 'block', position: 'fixed', zIndex: 9999 }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title">Save Filter Preset</h5>
              <button 
                type="button" 
                className="close" 
                aria-label="close" 
                onClick={onClose}
                disabled={isSaving}
                data-testid="save-filters-modal-close-button"
              >
                <span aria-hidden="true">×</span>
              </button>
            
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="presetName">Filter Name:</label>
                <input
                  type="text"
                  id="presetName"
                  className={`form-control ${validationError ? 'has-error' : ''}`}
                  value={presetName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter a name for this filter (1-100 characters)"
                  maxLength="101"
                  autoFocus
                  disabled={isSaving}
                  data-testid="save-filters-modal-name-input"
                />
                {validationError && (
                  <small className="form-validator-text-danger">
                    {validationError}
                  </small>
                )}
              </div>

              <div className="save-filters-modal__info">
                {currentConsist && (
                  <div className="save-filters-modal__info-section">
                    <p><strong>Consist Filter:</strong></p>
                    <div className="save-filters-modal__info-content">
                      <div className="save-filters-modal__info-item">
                        {currentConsist.title || currentConsist.value}
                      </div>
                    </div>
                  </div>
                )}

                {currentEvents && currentEvents.length > 0 && (
                  <div className="save-filters-modal__info-section">
                    <p><strong>Event Filters:</strong></p>
                    <div className="save-filters-modal__info-content">
                      {currentEvents.map((event) => (
                        <div key={event.value} className="save-filters-modal__info-item">
                          {event.title} – [{event.value}]
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {currentCars && currentCars.length > 0 && (
                  <div className="save-filters-modal__info-section">
                    <p><strong>Car Filters:</strong></p>
                    <div className="save-filters-modal__info-content">
                      {currentCars.map((car) => (
                        <div key={car} className="save-filters-modal__info-item">
                          Car: {car}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {currentSubsystems && currentSubsystems.length > 0 && (
                  <div className="save-filters-modal__info-section">
                    <p><strong>Subsystem Filters:</strong></p>
                    <div className="save-filters-modal__info-content">
                      {currentSubsystems.map((subsystem) => {
                        return (
                          <div key={subsystem.name} className="save-filters-modal__info-item">
                            {subsystem.name}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="save-filters-modal__info-section">
                  <p><strong>Environment variables query:</strong></p>
                  <pre data-testid="save-filters-modal-query-preview" className="save-filters-modal__query-preview">
                    {getFilterQueryPreview()}
                  </pre>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSaving}
                data-testid="save-filters-modal-cancel-button"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving || !presetName || !!validationError}
                data-testid="save-filters-modal-save-button"
              >
                {isSaving && <i className="fas fa-spinner fa-pulse"></i>}
                {isSaving ? ' Saving...' : 'Save Preset'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SaveFiltersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  presetName: PropTypes.string.isRequired,
  onPresetNameChange: PropTypes.func.isRequired,
  currentFilterQuery: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  currentCars: PropTypes.array,
  currentEvents: PropTypes.array,
  currentSubsystems: PropTypes.array,
  currentConsist: PropTypes.object,
  isSaving: PropTypes.bool
};
